/**
 * @file PromotionEditDialog.tsx
 * @description Create / edit акции-скидки (phase18.3, promo.md P1-P3).
 *
 * Состояние формы + валидация + сабмит; поля вынесены в
 * ./PromotionFormFields.tsx (лимит ≤200 строк/файл). Продуктового пикера
 * нет — id товара/SKU вводится вручную (подсказка «из URL карточки»).
 */

import { useEffect, useState } from "react";

import { catalogPromotions, type CatalogCategory, type CatalogPromotion } from "../../lib/api-catalog";
import { ApiError } from "../../lib/api-client";
import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../ui/dialog";
import { PromotionFormFields, type PromotionFormState } from "./PromotionFormFields";

/** ISO-строка → значение для <input type="datetime-local"> (локальное время). */
function toLocalInput(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  const local = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 16);
}

/** Значение datetime-local → ISO (UTC) для отправки на сервер. */
function toIso(local: string): string | null {
  if (!local.trim()) return null;
  const d = new Date(local);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function emptyForm(): PromotionFormState {
  return {
    displayName: "",
    kind: "PERCENT",
    value: "",
    scopeType: "CATALOG",
    categoryId: "",
    productId: "",
    skuId: "",
    channel: "",
    startsAt: toLocalInput(new Date().toISOString()),
    endsAt: "",
    isActive: true,
  };
}

function formFromPromotion(p: CatalogPromotion): PromotionFormState {
  return {
    displayName: p.displayName,
    kind: p.kind,
    value: String(p.value),
    scopeType: p.scopeType,
    categoryId: p.categoryId ?? "",
    productId: p.productId ?? "",
    skuId: p.skuId ?? "",
    channel: p.channel ?? "",
    startsAt: toLocalInput(p.startsAt),
    endsAt: toLocalInput(p.endsAt),
    isActive: p.isActive,
  };
}

interface Props {
  open: boolean;
  promotion: CatalogPromotion | null;
  categories: CatalogCategory[];
  onClose: () => void;
  onSaved: (promotion: CatalogPromotion) => void;
}

export function PromotionEditDialog({ open, promotion, categories, onClose, onSaved }: Props) {
  const isEdit = !!promotion;
  const [form, setForm] = useState<PromotionFormState>(emptyForm());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setForm(promotion ? formFromPromotion(promotion) : emptyForm());
    setError(null);
  }, [promotion, open]);

  function patch(p: Partial<PromotionFormState>) {
    setForm((prev) => ({ ...prev, ...p }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    if (!form.displayName.trim()) return setError("Название обязательно.");
    const numValue = Number.parseFloat(form.value.replace(",", "."));
    if (Number.isNaN(numValue) || numValue <= 0) return setError("Значение скидки должно быть больше нуля.");
    if (form.kind === "PERCENT" && (numValue < 1 || numValue > 100)) {
      return setError("Процентная скидка — от 1 до 100.");
    }
    if (form.scopeType === "CATEGORY" && !form.categoryId) return setError("Выберите категорию.");
    if (form.scopeType === "PRODUCT" && !form.productId.trim()) return setError("Укажите id товара.");
    if (form.scopeType === "SKU" && !form.skuId.trim()) return setError("Укажите id SKU.");
    if (!form.startsAt) return setError("Укажите дату начала.");

    const startsAtIso = toIso(form.startsAt);
    const endsAtIso = toIso(form.endsAt);
    if (!startsAtIso) return setError("Неверная дата начала.");
    if (endsAtIso && endsAtIso <= startsAtIso) return setError("Дата окончания должна быть позже даты начала.");

    setSubmitting(true);
    try {
      const payload = {
        displayName: form.displayName.trim(),
        kind: form.kind,
        value: numValue,
        scopeType: form.scopeType,
        categoryId: form.scopeType === "CATEGORY" ? form.categoryId : undefined,
        productId: form.scopeType === "PRODUCT" ? form.productId.trim() : undefined,
        skuId: form.scopeType === "SKU" ? form.skuId.trim() : undefined,
        channel: form.channel || null,
        startsAt: startsAtIso,
        endsAt: endsAtIso,
        isActive: form.isActive,
      };
      const saved = promotion
        ? await catalogPromotions.update(promotion.id, payload)
        : await catalogPromotions.create(payload);
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Не удалось сохранить");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent fullScreen className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Редактировать акцию" : "Новая акция"}</DialogTitle>
        </DialogHeader>

        <form
          id="promotion-form"
          onSubmit={(e) => void onSubmit(e)}
          className="flex flex-col gap-3 overflow-y-auto"
        >
          <PromotionFormFields form={form} categories={categories} disabled={submitting} onChange={patch} />
          {error ? <Alert severity="error">{error}</Alert> : null}
        </form>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>
            Отмена
          </Button>
          <Button type="submit" form="promotion-form" disabled={submitting}>
            {submitting ? "Сохраняем…" : isEdit ? "Сохранить" : "Создать"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
